import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Search, BarChart3, FileText } from 'lucide-react';
import { sortData } from '../components/SortableTableHeader';
import InteractiveTable from '../components/InteractiveTable';
import GRNDashboard from './GRN/GRNDashboard';
import ModuleReports from './Reports/ModuleReports';
import { GRN_REPORTS } from './Reports/reportConfigs';

export default function GoodsReceivedPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const setTab = (t) => setSearchParams({ tab: t });
  const [grnSearch, setGrnSearch] = useState('');

  const handleGrnNavigate = (tab, opts) => {
    if (opts?.search) setGrnSearch(opts.search);
    else setGrnSearch('');
    const params = { tab };
    if (opts?.status) params.status = opts.status;
    if (opts?.search) params.search = opts.search;
    setSearchParams(params);
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            <Package className="inline h-5 w-5 mr-2 text-primary" />
            Receiving
          </h1>
          <HelpButton guideId="goods-receiving" label="Receiving Guide" />
        </div>
      </div>
      <div className="flex gap-1 border-b mb-4" data-testid="grn-tabs">
        {[
          { id: 'dashboard', label: 'Analytics', icon: BarChart3 },
          { id: 'orders', label: 'Eligible Orders' },
          { id: 'reports', label: 'Reports', icon: FileText },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`grn-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            {t.icon && <t.icon className="h-3.5 w-3.5" />} {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'orders' && <GRNOrdersList initialSearch={grnSearch} />}
      {activeTab === 'dashboard' && <GRNDashboard onNavigate={handleGrnNavigate} />}
      {activeTab === 'reports' && <ModuleReports reports={GRN_REPORTS} apiPrefix="/api/module-reports/grn" moduleLabel="Receiving" />}
    </AppShell>
  );
}

function GRNOrdersList({ initialSearch }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch || '');
  const navigate = useNavigate();
  const [sortState, setSortState] = useState({ field: 'order_number', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);

  useEffect(() => {
    api.get('/api/grn/eligible-orders').then(r => setOrders(r.data.orders || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = orders.filter(o =>
    !search || o.order_number.toLowerCase().includes(search.toLowerCase()) ||
    (o.supplier_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (o.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const sortedFiltered = useMemo(() => sortData(filtered, sortState.field, sortState.dir), [filtered, sortState]);

  const grnDefaultCols = useMemo(() => [
    { key: 'order_number', label: 'Order', width: 120, sortField: 'order_number', visible: true },
    { key: 'supplier_name', label: 'Supplier', width: 180, sortField: 'supplier_name', visible: true },
    { key: 'description', label: 'Description', width: 220, sortField: 'description', visible: true },
    { key: 'net_total', label: 'Value', width: 120, sortField: 'net_total', align: 'right', visible: true },
    { key: 'line_count', label: 'Lines', width: 70, sortField: 'line_count', align: 'center', visible: true },
    { key: 'status', label: 'Status', width: 100, sortField: 'status', visible: true },
    { key: 'last_receipt', label: 'Last Receipt', width: 180, sortField: 'last_receipt_date', visible: true },
    { key: 'actions', label: '', width: 90, fixed: true, visible: true },
  ], []);

  const renderGrnCell = useCallback((col, o) => {
    switch (col.key) {
      case 'order_number': return <span className="font-mono text-zinc-900 font-semibold text-sm">{o.order_number}</span>;
      case 'supplier_name': return <span className="text-sm">{o.supplier_name || o.supplier_code || '—'}</span>;
      case 'description': return <span className="text-sm max-w-[200px] truncate block text-muted-foreground">{o.description || '—'}</span>;
      case 'net_total': return <span className="font-mono text-sm">${(o.net_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>;
      case 'line_count': return <span className="text-sm">{o.line_count}</span>;
      case 'status': return <Badge className={o.status === 'PARTIAL' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>{o.status}</Badge>;
      case 'last_receipt': return <span className="text-sm text-muted-foreground">{o.last_grn_number ? `${o.last_grn_number} (${o.last_receipt_date})` : '—'}</span>;
      case 'actions': return (
        <div onClick={e => e.stopPropagation()}>
          <Button size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => navigate(`/orders/${o.order_number}`)}>
            Receive
          </Button>
        </div>
      );
      default: return <span className="text-sm">{o[col.key] != null ? String(o[col.key]) : '—'}</span>;
    }
  }, [navigate]);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-muted-foreground">{filtered.length} eligible order(s) awaiting receipt</p>
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search orders..." className="pl-8 h-8 text-sm" />
        </div>
      </div>

      {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
        <EmptyState title="No eligible orders" description="All orders are either fully received, cancelled, or unissued." />
      ) : (
        <InteractiveTable
          tableId="grn-orders"
          defaultCols={grnDefaultCols}
          data={sortedFiltered}
          renderCell={renderGrnCell}
          onRowClick={(row) => navigate(`/orders/${row.order_number}`)}
          sortState={sortState}
          onSort={handleSort}
          rowKey="order_number"
        />
      )}
    </div>
  );
}
